import { useMemo, useState, type ComponentProps } from 'react';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { useRouteRefParams } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import type { ReviewStepProps } from '@backstage/plugin-scaffolder-react';
import type { JsonObject } from '@backstage/types';
import {
  ReviewState,
  scaffolderReactTranslationRef,
} from '@backstage/plugin-scaffolder-react/alpha';

const QTEST_MEMBER_TEMPLATE = 'qtest-member-addition';

const useStyles = makeStyles(
  theme => ({
    backButton: { marginRight: theme.spacing(1) },
    footer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(2),
    },
  }),
  { name: 'SanitizedScaffolderReviewStep' },
);

/**
 * Default scaffolder review step, with a tiny override for qtest-member-addition:
 * hide stale `userEmail` when "Raise request for a different user" is unchecked (RJSF
 * keeps the key in form state). Other templates behave like the built-in review step.
 */
export function SanitizedScaffolderReviewStep(props: ReviewStepProps) {
  const classes = useStyles();
  const { t } = useTranslationRef(scaffolderReactTranslationRef);
  const { templateName } = useRouteRefParams(scaffolderPlugin.routes.selectedTemplate);
  const [createBusy, setCreateBusy] = useState(false);

  const displayFormData = useMemo((): JsonObject => {
    const fd = props.formData;
    if (
      templateName !== QTEST_MEMBER_TEMPLATE ||
      fd.addMemberByEmail !== false ||
      typeof fd !== 'object' ||
      fd === null
    ) {
      return fd;
    }
    const { userEmail: _removed, ...rest } = fd;
    return rest as JsonObject;
  }, [templateName, props.formData]);

  const backLabel = t('stepper.backButtonText');
  const createLabel = t('stepper.createButtonText');

  const onCreate = async () => {
    setCreateBusy(true);
    try {
      await props.handleCreate();
    } finally {
      setCreateBusy(false);
    }
  };

  return (
    <>
      <ReviewState
        formState={displayFormData}
        schemas={props.steps as ComponentProps<typeof ReviewState>['schemas']}
      />
      <div className={classes.footer}>
        <Button
          onClick={props.handleBack}
          className={classes.backButton}
          disabled={props.disableButtons}
        >
          {backLabel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={props.disableButtons || createBusy}
          onClick={() => void onCreate()}
        >
          {createLabel}
        </Button>
      </div>
    </>
  );
}
